import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { adminApi } from '../services/adminApi';

export interface AdminUser {
  id: string;
  email: string;
  role: 'platform_admin';
  status: string;
  first_name: string;
  last_name: string;
}

interface AdminAuthContextType {
  user: AdminUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('admin_auth_token'));
  const [isLoading, setIsLoading] = useState(true);
  const initializedRef = useRef(false);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await adminApi.get('/auth/me');
      setUser(data.data);
    } catch {
      setToken(null);
      setUser(null);
      localStorage.removeItem('admin_auth_token');
    }
  }, []);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (token) {
      adminApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      refreshUser().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string) => {
    const { data } = await adminApi.post('/auth/admin-login', { email, password });
    const { token: newToken, user: userData } = data.data;
    localStorage.setItem('admin_auth_token', newToken);
    adminApi.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
  };

  const logout = useCallback(() => {
    adminApi.post('/auth/logout').catch(() => {});
    localStorage.removeItem('admin_auth_token');
    delete adminApi.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    initializedRef.current = false;
  }, []);

  return (
    <AdminAuthContext.Provider value={{ user, isLoading, isAuthenticated: !!user, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
};
